/**
 * Phase 126 — Venue Manage Surface at /arenax/host/venue/manage.
 *
 * This is the economic engine of ArenaX — supply liquidity command center.
 *
 *   Overview tiles  → today's bookings, occupancy %, revenue today, next slot, court count
 *   Court Management → add/rename/disable courts (Court A, 5v5 Turf, etc.)
 *   Weekly Schedule  → recurring availability template (Mon-Fri 6-23, weekend etc.)
 *   Slot Calendar    → Linear/Notion-style timeline grid, state mutation menu
 *
 * Capability-gated server-side via require_capability("publish_venue").
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import ArenaxSlotCalendar from './ArenaxSlotCalendar';
import ArenaxBookingInbox from './ArenaxBookingInbox';
import OperatorCockpit from '@/components/arenax/OperatorCockpit';
import CourtRow from '@/components/arenax/CourtRow';
import CourtAuditDrawer from '@/components/arenax/CourtAuditDrawer';
import {
  BulletproofMenu,
  BulletproofMenuTrigger,
  BulletproofMenuContent,
  DropdownItem,
  DropdownSectionLabel,
  DropdownDivider,
} from '@/components/ui/bulletproof-menu';
import {
  MapPin, ArrowLeft, Plus, X, Loader2, Calendar, IndianRupee,
  Percent, Building, BarChart3, Sparkles, ChevronDown, Check,
} from 'lucide-react';

const LAST_VENUE_KEY = 'arenax_host_last_venue_id';

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function defaultWindowsForDay(dow) {
  // 06:00 – 22:00 weekdays, 07:00 – 23:00 weekends, evening 18:00+ is peak
  const isWeekend = dow >= 5;
  return [
    { day_of_week: dow, start_minute: isWeekend ? 420 : 360, end_minute: 1080, is_peak: false },
    { day_of_week: dow, start_minute: 1080, end_minute: isWeekend ? 1380 : 1320, is_peak: true },
  ];
}

export default function ArenaxHostVenueManage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const queryVenueId = sp.get('venue_id');

  const [venueId, setVenueId] = useState(queryVenueId);
  const [allVenues, setAllVenues] = useState([]);
  const [overview, setOverview] = useState(null);
  const [courts, setCourts] = useState([]);
  const [selectedCourtId, setSelectedCourtId] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newCourt, setNewCourt] = useState({ name: '', sport: 'Football' });
  const [showCourtForm, setShowCourtForm] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState(null);
  // Phase 178.6.H — court lifecycle UI state
  const [courtFilter, setCourtFilter] = useState('active');   // active | hidden | archived | maintenance | deleted
  const [courtCounts, setCourtCounts] = useState({ active: 0, hidden: 0, archived: 0, maintenance: 0, deleted: 0, total: 0 });
  const [auditCourt, setAuditCourt] = useState(null);

  // Resolve venue id: URL > localStorage > /me/venues primary
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const vr = await api.get('/arenax/host/me/venues');
        const venues = vr.data?.venues || [];
        setAllVenues(venues);
        const fromUrl = queryVenueId;
        const fromStorage = localStorage.getItem(LAST_VENUE_KEY);
        const fromUrlExists = fromUrl && venues.some(v => v.id === fromUrl);
        const fromStorageExists = fromStorage && venues.some(v => v.id === fromStorage);
        const chosen = (fromUrlExists ? fromUrl
                       : fromStorageExists ? fromStorage
                       : venues[0]?.id) || null;
        if (chosen && chosen !== venueId) setVenueId(chosen);
        if (chosen) {
          localStorage.setItem(LAST_VENUE_KEY, chosen);
          boot(chosen);
        } else {
          setLoading(false);
        }
      } catch (_e) { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, queryVenueId]);

  const switchVenue = (id) => {
    localStorage.setItem(LAST_VENUE_KEY, id);
    setVenueId(id);
    setSelectedCourtId(null);
    boot(id);
  };

  const boot = async (vid) => {
    setLoading(true);
    try {
      const [ovr, cts, tplR, cnts] = await Promise.all([
        api.get(`/arenax/host/venue/${vid}/overview`),
        api.get(`/arenax/host/venue/${vid}/courts?include_archived=true`),
        api.get(`/arenax/host/venue/${vid}/slot-template`),
        api.get(`/arenax/host/venue/${vid}/court-status-counts`).catch(() => ({ data: null })),
      ]);
      setOverview(ovr.data);
      setCourts(cts.data?.courts || []);
      setSelectedCourtId(prev => prev || cts.data?.courts?.find(c => (c.status || 'active') === 'active')?.id || cts.data?.courts?.[0]?.id || null);
      setTemplate(tplR.data?.template || null);
      setScheduleDraft(tplR.data?.template || null);
      if (cnts.data) {
        setCourtCounts({
          ...cnts.data.counts,
          total: cnts.data.total ?? 0,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load venue');
    } finally { setLoading(false); }
  };

  const refreshCourtCounts = async () => {
    if (!venueId) return;
    try {
      const r = await api.get(`/arenax/host/venue/${venueId}/court-status-counts`);
      setCourtCounts({ ...r.data.counts, total: r.data.total ?? 0 });
    } catch (_e) { /* non-fatal */ }
  };

  const addCourt = async () => {
    if (!(newCourt.name || '').trim()) { toast.error('Court name required'); return; }
    try {
      const r = await api.post(`/arenax/host/venue/${venueId}/courts`, newCourt);
      setCourts(prev => [...prev, r.data.court]);
      setSelectedCourtId(prev => prev || r.data.court.id);
      setNewCourt({ name: '', sport: 'Football' });
      setShowCourtForm(false);
      toast.success('Court added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add court');
    }
  };

  const saveSchedule = async () => {
    if (!scheduleDraft) return;
    setBusy(true);
    try {
      const payload = {
        court_id: null,
        slot_duration_minutes: scheduleDraft.slot_duration_minutes || 60,
        windows: scheduleDraft.windows || [],
        base_price_default: scheduleDraft.base_price_default,
        peak_price_default: scheduleDraft.peak_price_default,
      };
      const r = await api.post(`/arenax/host/venue/${venueId}/slot-template`, payload);
      setTemplate(r.data.template);
      toast.success('Weekly schedule saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save schedule');
    } finally { setBusy(false); }
  };

  const generateSlots = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/arenax/host/venue/${venueId}/slots/generate?days=14`);
      toast.success(`${r.data.generated} new slots · ${r.data.skipped} already existed`);
      // Refresh overview
      const ovr = await api.get(`/arenax/host/venue/${venueId}/overview`);
      setOverview(ovr.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate slots');
    } finally { setBusy(false); }
  };

  if (!user) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <MapPin className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            Sign in to manage your venue
          </h1>
          <button onClick={() => navigate('/arenax/auth/login')}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}>
            Sign in
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  if (loading) {
    return (
      <ArenaxLayout>
        <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin"
             style={{ color: 'hsl(var(--ax-accent-cyan))' }} /></div>
      </ArenaxLayout>
    );
  }

  if (!venueId) {
    return (
      <ArenaxLayout>
        <section className="max-w-md mx-auto px-5 py-20 text-center arenax-themed">
          <MapPin className="h-10 w-10 mx-auto mb-3" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
          <h1 className="text-2xl font-bold mb-2"
              style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, serif' }}>
            You don't have a venue yet
          </h1>
          <p className="text-sm mb-6" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            List a venue first to unlock the operator command center.
          </p>
          <button onClick={() => navigate('/arenax/host/venue')}
                  className="px-5 py-2.5 rounded-xl text-sm font-bold"
                  style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                  data-testid="venue-manage-list-cta">
            List your venue
          </button>
        </section>
      </ArenaxLayout>
    );
  }

  return (
    <ArenaxLayout>
      <section className="max-w-6xl mx-auto px-5 py-8 arenax-themed space-y-8" data-testid="arenax-venue-manage-page">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <button onClick={() => navigate('/arenax/host')}
                    className="text-xs font-semibold flex items-center gap-1 mb-2"
                    style={{ color: 'hsl(var(--ax-text-muted))' }}
                    data-testid="venue-manage-back">
              <ArrowLeft className="h-3 w-3" /> Back to hub
            </button>
            <div className="relative inline-block">
              {allVenues.length > 1 ? (
                <BulletproofMenu className="relative inline-block">
                  <BulletproofMenuTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 group"
                      data-testid="venue-manage-picker-toggle"
                    >
                      <h1 className="text-3xl sm:text-4xl font-light tracking-tight"
                          style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, "Times New Roman", serif' }}
                          data-testid="venue-manage-header-name">
                        {overview?.venue_name
                          || allVenues.find(v => v.id === venueId)?.name
                          || 'Your venue'}
                      </h1>
                      <ChevronDown className="h-5 w-5 transition-transform group-hover:translate-y-0.5"
                                   style={{ color: 'hsl(var(--ax-text-muted))' }} />
                    </button>
                  </BulletproofMenuTrigger>
                  <BulletproofMenuContent
                    className="absolute top-full left-0 mt-2 z-20 w-72 arenax-card rounded-xl py-2 shadow-xl"
                    data-testid="venue-manage-picker-menu"
                  >
                    <DropdownSectionLabel testid="venue-manage-picker-label">
                      Switch venue
                    </DropdownSectionLabel>
                    {allVenues.map(v => (
                      <DropdownItem
                        key={v.id}
                        onSelect={() => switchVenue(v.id)}
                        active={v.id === venueId}
                        iconColor={v.id === venueId ? 'hsl(var(--ax-accent-cyan))' : undefined}
                        trailing={v.id === venueId
                          ? <Check className="h-4 w-4 flex-shrink-0"
                                   style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
                          : null}
                        source="venue_switcher"
                        testid={`venue-manage-picker-${v.id}`}
                      >
                        <div className="min-w-0">
                          <div className="font-bold truncate"
                               style={{ color: v.id === venueId ? 'hsl(var(--ax-accent-cyan))' : 'hsl(var(--ax-text))' }}>
                            {v.name}
                          </div>
                          <div className="text-[10px] truncate"
                               style={{ color: 'hsl(var(--ax-text-muted))' }}>
                            {v.area ? `${v.area} · ` : ''}{v.city} · {(v.entity_type || '').replace('_', ' ')}
                          </div>
                        </div>
                      </DropdownItem>
                    ))}
                    <DropdownDivider />
                    <DropdownItem
                      href="/arenax/host/venue"
                      icon={Plus}
                      iconAccent
                      source="venue_switcher"
                      testid="venue-manage-picker-add"
                    >
                      Add another venue
                    </DropdownItem>
                  </BulletproofMenuContent>
                </BulletproofMenu>
              ) : (
                <h1 className="text-3xl sm:text-4xl font-light tracking-tight"
                    style={{ color: 'hsl(var(--ax-text))', fontFamily: 'Georgia, "Times New Roman", serif' }}
                    data-testid="venue-manage-header-name">
                  {overview?.venue_name
                    || allVenues.find(v => v.id === venueId)?.name
                    || 'Your venue'}
                </h1>
              )}
            </div>
            <p className="text-sm mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>
              Inventory · Bookings · Revenue · The city's rhythm runs through here.
            </p>
          </div>
        </div>

        {/* Overview tiles */}
        {/* Operator Cockpit — Phase 178.6 (replaces flat tile grid) */}
        <OperatorCockpit kind="venue" entityId={venueId} overview={overview} />

        {/* Court Management */}
        <div className="arenax-card arenax-operational-form p-6" style={{ borderRadius: 16 }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ color: 'hsl(var(--ax-text))' }}>
                Courts
              </h2>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                Add each bookable surface. A 4-court badminton hall = 4 courts.
              </p>
            </div>
            <button onClick={() => setShowCourtForm(s => !s)}
                    className="text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 transition-all hover:-translate-y-0.5"
                    style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                    data-testid="venue-manage-add-court-toggle">
              <Plus className="h-3.5 w-3.5" /> Add court
            </button>
          </div>

          {showCourtForm && (
            <div className="mb-4 p-4 rounded-xl border" style={{ borderColor: 'var(--ax-border-quiet)', background: 'var(--ax-card-bg-soft)' }}
                 data-testid="venue-manage-court-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  data-testid="venue-manage-new-court-name"
                  value={newCourt.name}
                  onChange={e => setNewCourt(c => ({ ...c, name: e.target.value }))}
                  placeholder="Court A · 5v5 Turf · Badminton Hall"
                  className="arenax-input px-3 py-2 rounded-lg text-sm col-span-1 sm:col-span-2"
                />
                <input
                  data-testid="venue-manage-new-court-sport"
                  value={newCourt.sport}
                  onChange={e => setNewCourt(c => ({ ...c, sport: e.target.value }))}
                  placeholder="Sport (Football, Badminton…)"
                  className="arenax-input px-3 py-2 rounded-lg text-sm"
                />
              </div>
              <div className="flex justify-end mt-3 gap-2">
                <button onClick={() => { setShowCourtForm(false); setNewCourt({ name: '', sport: 'Football' }); }}
                        className="text-xs px-3 py-1.5 rounded-lg"
                        style={{ color: 'hsl(var(--ax-text-muted))' }}>
                  Cancel
                </button>
                <button onClick={addCourt}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg"
                        style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                        data-testid="venue-manage-new-court-submit">
                  Add court
                </button>
              </div>
            </div>
          )}

          {courts.length === 0 ? (
            <p className="text-xs italic py-6 text-center"
               style={{ color: 'hsl(var(--ax-text-faint))' }}>
              No courts yet. Add your first to unlock the slot calendar.
            </p>
          ) : (
            <>
              {/* Phase 178.6.H — Lifecycle filter chips */}
              <div className="flex items-center gap-1.5 mb-3 overflow-x-auto pb-1"
                   data-testid="venue-manage-court-filters">
                {[
                  { id: 'active',      label: 'Active' },
                  { id: 'hidden',      label: 'Hidden' },
                  { id: 'maintenance', label: 'Maintenance' },
                  { id: 'archived',    label: 'Archived' },
                  ...(user?.role === 'super_admin' ? [{ id: 'deleted', label: 'Deleted' }] : []),
                ].map((f) => {
                  const count = courtCounts[f.id] || 0;
                  const isSel = courtFilter === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setCourtFilter(f.id)}
                      className="text-[11px] font-bold px-3 py-1 rounded-full transition-colors whitespace-nowrap"
                      style={{
                        background: isSel ? 'hsl(var(--ax-text))' : 'var(--ax-card-bg-soft)',
                        color: isSel ? 'hsl(var(--ax-card-bg-soft))' : 'hsl(var(--ax-text-muted))',
                        border: '1px solid var(--ax-border-quiet)',
                        opacity: count === 0 && !isSel ? 0.5 : 1,
                      }}
                      data-testid={`venue-manage-court-filter-${f.id}`}
                    >
                      {f.label} <span className="opacity-70 ml-1">{count}</span>
                    </button>
                  );
                })}
              </div>
              {(() => {
                const visible = courts.filter(c => (c.status || (c.is_active ? 'active' : 'archived')) === courtFilter);
                if (visible.length === 0) {
                  return (
                    <p className="text-xs italic py-6 text-center"
                       style={{ color: 'hsl(var(--ax-text-faint))' }}
                       data-testid="venue-manage-court-filter-empty">
                      No {courtFilter} courts.
                    </p>
                  );
                }
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {visible.map(c => (
                      <CourtRow
                        key={c.id}
                        court={c}
                        venueId={venueId}
                        onChange={(updated) => {
                          setCourts(prev => {
                            if (updated?._removed) return prev.filter(x => x.id !== c.id);
                            return prev.map(x => x.id === c.id ? { ...x, ...updated } : x);
                          });
                          if (updated?._removed && selectedCourtId === c.id) {
                            setSelectedCourtId(courts.find(x => x.id !== c.id)?.id || null);
                          }
                          refreshCourtCounts();
                        }}
                        onViewHistory={() => setAuditCourt(c)}
                      />
                    ))}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* Weekly Schedule */}
        <div className="arenax-card arenax-operational-form p-6" style={{ borderRadius: 16 }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ color: 'hsl(var(--ax-text))' }}>
                Weekly availability
              </h2>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                Set once. Generate slots every fortnight. Players see the same rhythm.
              </p>
            </div>
            <button onClick={saveSchedule}
                    disabled={busy || !scheduleDraft}
                    className="text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-60"
                    style={{ background: 'var(--ax-primary-gradient)', color: 'hsl(var(--ax-button-text))' }}
                    data-testid="venue-manage-save-schedule">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Save weekly schedule
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <PriceField label="Default ₹ / hr"
                        testId="venue-manage-base-price"
                        value={scheduleDraft?.base_price_default ?? ''}
                        onChange={v => setScheduleDraft(s => ({ ...(s || {}), base_price_default: v }))} />
            <PriceField label="Peak ₹ / hr"
                        testId="venue-manage-peak-price"
                        value={scheduleDraft?.peak_price_default ?? ''}
                        onChange={v => setScheduleDraft(s => ({ ...(s || {}), peak_price_default: v }))} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-7 gap-2" data-testid="venue-manage-day-grid">
            {DAYS_OF_WEEK.map((d, dow) => {
              const windows = (scheduleDraft?.windows || []).filter(w => w.day_of_week === dow);
              const isEnabled = windows.length > 0;
              return (
                <div key={d}
                     className="px-3 py-3 rounded-lg border"
                     style={{ borderColor: isEnabled ? 'rgba(16,185,129,0.40)' : 'var(--ax-border-quiet)',
                              background: isEnabled ? 'rgba(16,185,129,0.04)' : 'var(--ax-card-bg-soft)' }}
                     data-testid={`venue-manage-day-${d.toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] uppercase tracking-wider font-bold"
                          style={{ color: 'hsl(var(--ax-text))' }}>{d}</span>
                    <label className="cursor-pointer">
                      <input type="checkbox"
                             checked={isEnabled}
                             onChange={(e) => {
                               setScheduleDraft(s => {
                                 const others = (s?.windows || []).filter(w => w.day_of_week !== dow);
                                 return {
                                   ...(s || {}),
                                   slot_duration_minutes: s?.slot_duration_minutes || 60,
                                   windows: e.target.checked ? [...others, ...defaultWindowsForDay(dow)] : others,
                                 };
                               });
                             }}
                             className="h-3 w-3 cursor-pointer" />
                    </label>
                  </div>
                  {isEnabled ? (
                    <div className="space-y-1">
                      {windows.map((w, i) => (
                        <div key={i} className="text-[10px] flex items-center justify-between gap-1"
                             style={{ color: 'hsl(var(--ax-text-muted))' }}>
                          <span>{minToHHMM(w.start_minute)}–{minToHHMM(w.end_minute)}</span>
                          {w.is_peak && <span style={{ color: '#d97706' }}>PEAK</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] italic" style={{ color: 'hsl(var(--ax-text-faint))' }}>Closed</p>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[11px] mt-3" style={{ color: 'hsl(var(--ax-text-faint))' }}>
            Defaults: weekdays 06:00–22:00 with 18:00+ as peak; weekends 07:00–23:00. Customise per-day windows in a future release.
          </p>
        </div>

        {/* Booking Inbox — Phase 127 demand-side requests */}
        <ArenaxBookingInbox
          venueId={venueId}
          onAcceptedRefresh={() => boot(venueId)}
        />

        {/* Slot Calendar */}
        <div className="arenax-card p-6" style={{ borderRadius: 16 }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-bold tracking-tight" style={{ color: 'hsl(var(--ax-text))' }}>
                Slot calendar
              </h2>
              <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--ax-text-muted))' }}>
                Generated from the weekly schedule. Click a slot to block, unblock, or confirm.
              </p>
            </div>
            <div className="text-[10px] uppercase tracking-wider" style={{ color: 'hsl(var(--ax-text-faint))' }}>
              <BarChart3 className="h-3 w-3 inline mr-1" /> Live grid
            </div>
          </div>
          <ArenaxSlotCalendar
            venueId={venueId}
            courts={courts}
            selectedCourtId={selectedCourtId}
            onSelectCourt={setSelectedCourtId}
            onRequestGenerate={generateSlots}
            onRefresh={() => boot(venueId)}
            busy={busy}
          />
        </div>
      </section>

      {/* Phase 178.6.H — Court lifecycle audit drawer */}
      <CourtAuditDrawer
        court={auditCourt}
        venueId={venueId}
        open={!!auditCourt}
        onClose={() => setAuditCourt(null)}
      />
    </ArenaxLayout>
  );
}

function Tile({ icon: Icon, label, value, hint }) {
  return (
    <div className="arenax-card p-4 rounded-xl" data-testid={`venue-tile-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider font-bold"
              style={{ color: 'hsl(var(--ax-text-faint))' }}>{label}</span>
        <Icon className="h-3.5 w-3.5" style={{ color: 'hsl(var(--ax-accent-cyan))' }} />
      </div>
      <div className="text-xl font-bold tracking-tight" style={{ color: 'hsl(var(--ax-text))' }}>
        {value}
      </div>
      {hint && <div className="text-[10px] mt-1" style={{ color: 'hsl(var(--ax-text-muted))' }}>{hint}</div>}
    </div>
  );
}

function PriceField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[11px] tracking-wider uppercase font-bold block mb-1.5"
             style={{ color: 'hsl(var(--ax-text-muted))' }}>
        {label}
      </label>
      <input
        type="number" min={0}
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value ? Number(e.target.value) : null)}
        placeholder="1200"
        className="arenax-input w-full px-3 py-2 rounded-lg text-sm"
      />
    </div>
  );
}

function minToHHMM(m) {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}
